import { Plan } from '../types';

interface Props {
  form: Omit<Plan, 'id'>;
  set: (f: Partial<Omit<Plan, 'id'>>) => void;
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 transition-colors';

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button type="button" onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-violet-600' : 'bg-white/10'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
  </button>
);

const Row = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
    <div>
      <p className="text-[13px] text-white/70">{label}</p>
      {hint && <p className="text-[11px] text-white/30">{hint}</p>}
    </div>
    <div>{children}</div>
  </div>
);

const overdueBehaviors = [
  { value: 'downgrade', label: 'Fazer downgrade automático para plano gratuito' },
  { value: 'suspend', label: 'Suspender acesso até pagamento' },
  { value: 'restrict', label: 'Restringir apenas novas ações (leitura permitida)' },
  { value: 'notify_only', label: 'Apenas notificar (sem bloquear acesso)' },
];

const PlanEditorBilling = ({ form, set }: Props) => (
  <div>
    {/* Billing modes */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Modalidades de Cobrança Habilitadas</p>
      <p className="text-[12px] text-white/40 mb-4">Defina quais periodicidades de pagamento estão disponíveis para este plano no checkout.</p>
      <Row label="Cobrança mensal habilitada" hint="Usuário pode escolher pagar mensalmente">
        <Toggle value={form.monthly_billing_enabled} onChange={(v) => set({ monthly_billing_enabled: v })} />
      </Row>
      <Row label="Cobrança anual habilitada" hint="Usuário pode escolher pagar anualmente com desconto">
        <Toggle value={form.annual_billing_enabled} onChange={(v) => set({ annual_billing_enabled: v })} />
      </Row>
      <Row label="Recorrência habilitada" hint="Cobranças se repetem automaticamente (assinatura)">
        <Toggle value={form.auto_renewal_default} onChange={(v) => set({ auto_renewal_default: v })} />
      </Row>
    </div>

    {/* Default renewal */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Renovação Automática</p>
      <Row label="Renovação automática ativa por padrão" hint="Usuário pode desativar a qualquer momento no perfil">
        <Toggle value={form.auto_renewal_default} onChange={(v) => set({ auto_renewal_default: v })} />
      </Row>
    </div>

    {/* Grace period */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5 mb-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Tolerância e Inadimplência</p>
      <div className="mb-4">
        <label className="block text-[12px] text-white/50 mb-1.5">
          Dias de tolerância após vencimento
          <span className="ml-2 text-white/30 text-[11px]">— período antes de aplicar penalidade</span>
        </label>
        <input
          type="number" min={0} max={30}
          value={form.grace_days}
          onChange={(e) => set({ grace_days: Number(e.target.value) })}
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-[12px] text-white/50 mb-1.5">Comportamento após inadimplência</label>
        <select
          value={form.overdue_behavior}
          onChange={(e) => set({ overdue_behavior: e.target.value })}
          className={`${inputCls} cursor-pointer`}
        >
          {overdueBehaviors.map((b) => (
            <option key={b.value} value={b.value} className="bg-[#1A1A2E]">{b.label}</option>
          ))}
        </select>
        <p className="text-[11px] text-white/30 mt-1.5">
          Aplicado após o vencimento do período de tolerância
        </p>
      </div>
    </div>

    {/* Summary */}
    <div className="bg-white/3 border border-white/8 rounded-2xl p-5">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Resumo das Configurações</p>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-white/50">Mensal disponível</span>
          <span className={form.monthly_billing_enabled ? 'text-emerald-400' : 'text-red-400'}>{form.monthly_billing_enabled ? 'Sim' : 'Não'}</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-white/50">Anual disponível</span>
          <span className={form.annual_billing_enabled ? 'text-emerald-400' : 'text-red-400'}>{form.annual_billing_enabled ? 'Sim' : 'Não'}</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-white/50">Renovação automática padrão</span>
          <span className={form.auto_renewal_default ? 'text-emerald-400' : 'text-amber-400'}>{form.auto_renewal_default ? 'Ativa' : 'Inativa'}</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-white/50">Tolerância</span>
          <span className="text-white">{form.grace_days} dia(s)</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-white/50">Em caso de inadimplência</span>
          <span className="text-white">{overdueBehaviors.find((b) => b.value === form.overdue_behavior)?.label.split(' ').slice(0, 3).join(' ') + '...'}</span>
        </div>
      </div>
    </div>
  </div>
);

export default PlanEditorBilling;
